import type { ComponentVisibility } from '@commenda/types';

export interface SingleCondition {
  field?: string;
  operator?: string;
  value?: unknown;
}

export interface ConditionGroup {
  id: string;
  operator: 'and' | 'or';
  conditions: SingleCondition[];
}

export const generateGroupId = () =>
  `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export function parseVisibilityToGroups(visibility?: ComponentVisibility): {
  topLevelOperator: 'and' | 'or';
  groups: ConditionGroup[];
} {
  if (!visibility?.condition) {
    return { topLevelOperator: 'and', groups: [] };
  }

  const { condition } = visibility;

  if (condition.field) {
    return {
      topLevelOperator: 'and',
      groups: [
        {
          id: generateGroupId(),
          operator: 'and',
          conditions: [
            {
              field: condition.field,
              operator: condition.operator,
              value: condition.value,
            },
          ],
        },
      ],
    };
  }

  const topLevelOperator: 'and' | 'or' = condition.and ? 'and' : 'or';
  const topLevelConditions = condition.and || condition.or || [];

  const groups: ConditionGroup[] = [];

  topLevelConditions.forEach((cond) => {
    if (cond.and || cond.or) {
      const groupOperator: 'and' | 'or' = cond.and ? 'and' : 'or';
      const groupConditions = cond.and || cond.or || [];
      groups.push({
        id: generateGroupId(),
        operator: groupOperator,
        conditions: groupConditions.map((c) => ({
          field: c.field,
          operator: c.operator,
          value: c.value,
        })),
      });
    } else if (cond.field) {
      groups.push({
        id: generateGroupId(),
        operator: 'and',
        conditions: [
          {
            field: cond.field,
            operator: cond.operator,
            value: cond.value,
          },
        ],
      });
    }
  });

  if (groups.length === 0 && topLevelConditions.length > 0) {
    groups.push({
      id: generateGroupId(),
      operator: topLevelOperator,
      conditions: topLevelConditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
      })),
    });
  }

  return { topLevelOperator, groups };
}

export function groupsToVisibility(
  topLevelOperator: 'and' | 'or',
  groups: ConditionGroup[]
): ComponentVisibility | undefined {
  const validGroups = groups.filter(
    (g) =>
      g.conditions.length > 0 && g.conditions.some((c) => c.field && c.operator)
  );

  if (validGroups.length === 0) {
    return undefined;
  }

  if (validGroups.length === 1 && validGroups[0].conditions.length === 1) {
    const cond = validGroups[0].conditions[0];
    return {
      condition: {
        field: cond.field,
        operator: cond.operator as ComponentVisibility['condition']['operator'],
        value: cond.value,
      },
    };
  }

  if (validGroups.length === 1) {
    const group = validGroups[0];
    const conditionsArray = group.conditions.map((c) => ({
      field: c.field,
      operator: c.operator as ComponentVisibility['condition']['operator'],
      value: c.value,
    }));

    if (group.operator === 'and') {
      return { condition: { and: conditionsArray } };
    }
    return { condition: { or: conditionsArray } };
  }

  const topLevelConditions = validGroups.map((group) => {
    if (group.conditions.length === 1) {
      const c = group.conditions[0];
      return {
        field: c.field,
        operator: c.operator as ComponentVisibility['condition']['operator'],
        value: c.value,
      };
    }

    const conditionsArray = group.conditions.map((c) => ({
      field: c.field,
      operator: c.operator as ComponentVisibility['condition']['operator'],
      value: c.value,
    }));

    if (group.operator === 'and') {
      return { and: conditionsArray };
    }
    return { or: conditionsArray };
  });

  if (topLevelOperator === 'and') {
    return { condition: { and: topLevelConditions } };
  }
  return { condition: { or: topLevelConditions } };
}
